/**
 * Internal admin notes — list / add / edit / delete. Visible only on
 * the admin review page; the customer never sees this section or any
 * notes data.
 */

import { useEffect, useState } from "react";

import { createNote, deleteNote, listNotes, updateNote } from "@starz/shared/api/sdk.gen";
import type { Note } from "@starz/shared/api/types.gen";

interface Props {
  estimateId: string;
}

export function NotesPanel({ estimateId }: Props) {
  const [notes, setNotes] = useState<Note[]>([]);
  const [draft, setDraft] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingBody, setEditingBody] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await listNotes({ estimateId });
      if (cancelled) return;
      if ("data" in result) {
        setNotes(result.data);
      } else {
        setError(result.error.error.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [estimateId]);

  async function onAdd() {
    const body = draft.trim();
    if (!body) {
      setError("Note body is required.");
      return;
    }
    setBusy("create");
    setError(null);
    const result = await createNote({ estimateId, body: { body } });
    setBusy(null);
    if ("error" in result) {
      setError(result.error.error.message);
      return;
    }
    setNotes((prev) => [result.data, ...prev]);
    setDraft("");
  }

  async function onSaveEdit(noteId: string) {
    const body = editingBody.trim();
    if (!body) {
      setError("Note body is required.");
      return;
    }
    setBusy(noteId);
    setError(null);
    const result = await updateNote({
      estimateId,
      noteId,
      body: { body },
    });
    setBusy(null);
    if ("error" in result) {
      setError(result.error.error.message);
      return;
    }
    setNotes((prev) => prev.map((n) => (n.note_id === noteId ? result.data : n)));
    setEditingId(null);
    setEditingBody("");
  }

  async function onDelete(noteId: string) {
    if (!window.confirm("Delete this note?")) return;
    setBusy(noteId);
    setError(null);
    const result = await deleteNote({ estimateId, noteId });
    setBusy(null);
    if ("error" in result) {
      setError(result.error.error.message);
      return;
    }
    setNotes((prev) => prev.filter((n) => n.note_id !== noteId));
  }

  return (
    <section
      aria-label="Internal notes"
      className="mb-6 space-y-4 rounded-xl border border-starz-gold/20 bg-white p-5 shadow-sm"
    >
      <header className="flex items-baseline justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-starz-gold">Internal notes</p>
          <p className="mt-1 text-xs text-warm-gray">Admin-only · The customer never sees these.</p>
        </div>
      </header>
      <div className="space-y-2">
        <label className="block text-xs font-semibold uppercase tracking-widest text-starz-charcoal">
          Add a note
        </label>
        <textarea
          rows={2}
          maxLength={2000}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="e.g. Verified drainage on site visit; margin OK at 28%"
          className="w-full rounded-lg border border-starz-gold/30 bg-white p-3 text-sm shadow-inner focus:border-starz-gold focus:outline-none"
        />
        <button
          type="button"
          onClick={onAdd}
          disabled={busy === "create" || draft.trim().length === 0}
          className="min-h-[44px] rounded-lg bg-starz-navy px-4 py-2 text-sm font-semibold uppercase tracking-wide text-starz-off-white disabled:opacity-60"
        >
          {busy === "create" ? "Saving…" : "Add note"}
        </button>
      </div>
      {error && (
        <p className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {error}
        </p>
      )}
      <ul className="space-y-3">
        {notes.length === 0 && <li className="text-xs text-warm-gray">No notes yet.</li>}
        {notes.map((n) => (
          <li key={n.note_id} className="rounded border border-starz-gold/15 bg-starz-cream/40 p-3">
            {editingId === n.note_id ? (
              <div className="space-y-2">
                <textarea
                  rows={3}
                  maxLength={2000}
                  value={editingBody}
                  onChange={(e) => setEditingBody(e.target.value)}
                  className="w-full rounded border border-starz-gold/30 bg-white p-3 text-sm"
                />
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => onSaveEdit(n.note_id)}
                    disabled={busy === n.note_id}
                    className="min-h-[36px] rounded bg-starz-navy px-3 py-1 text-xs font-semibold uppercase tracking-wide text-starz-off-white disabled:opacity-60"
                  >
                    {busy === n.note_id ? "Saving…" : "Save"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingId(null);
                      setEditingBody("");
                    }}
                    className="min-h-[36px] rounded border border-starz-gold/30 px-3 py-1 text-xs text-starz-charcoal"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <p className="whitespace-pre-wrap text-sm text-starz-charcoal">{n.body}</p>
                <div className="mt-2 flex items-center justify-between text-[10px] uppercase tracking-widest text-warm-gray">
                  <span>{new Date(n.updated_at).toLocaleString()}</span>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setEditingId(n.note_id);
                        setEditingBody(n.body);
                      }}
                      className="font-semibold text-starz-gold hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(n.note_id)}
                      disabled={busy === n.note_id}
                      className="font-semibold text-rose-700 hover:underline disabled:opacity-60"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
